import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import NProgress from 'nprogress'

import { useAppStore } from '@/store/app'
import { useUserStore } from '@/store/user'
import { packageAuthService } from '@/api'

import PortalService from '@/services/portalService'
import BusinessProfileService from '@/services/businessProfileService'

import InvalidPortalError from '@/errors/InvalidPortalError'
import InvalidBusinessProfileRequestedError from '@/errors/InvalidBusinessProfileRequestedError'
import { isForbiddenPortalError } from '@/api/errorHandler'

// Routes

import authRoutes from '@/views/Authentication/router'
import samplePageRoutes from '@/views/Home/router'
import AudienceListingRoute from '@/views/AudienceListing/AudienceListingRoute'
import AudienceCreationRoute from '@/views/AudienceCreation/AudienceCreationRoute'

import productSetRoutes from '@/views/ProductsetListing/router'
import productInsightsRoutes from '@/views/ProductInsignts/router'

import productsetCreationRoutes from '@/views/ProductsetCreation/router'
import catalogManagerRoutes from '@/views/CatalogManager/router'
import productVariantsRoutes from '@/views/ProductVariants/router'
import sourceCatalogListingRoute from '@/views/SourceCatalogListing/SourceCatalogRouter'
import dashboardRoutes from '@/views/DashboardListing/router'
import stopLossConfigurationsRoute from '@/views/StopLossConfiguration/router'

import oAuthRedirectRoutes from '@/views/OAuthRedirect/router'
import signupRoutes from '@/views/Signup/router'
import editAudienceRoute from '@/views/EditAudience/editAudienceRoute'
import creativeAutomationListingRoute from '@/views/CreativeAutomation/router'

import {
  TimeZoneService,
  CurrencyService,
  RoimaticService,
  UserService
} from '@/services'

// services
const roimaticService = new RoimaticService()
const userService = new UserService()

const publicRoutes = [
  'login',
  'callback',
  'health',
  'NotFound',
  'NoAccess',
  'OAuthRedirect',
  'OAuthRedirectView',
  'Signup'
]

const routes: RouteRecordRaw[] = [
  {
    name: 'health',
    path: '/app/health',
    component: () => import('@/views/HealthCheck/HealthCheck.vue')
  },
  {
    name: 'login',
    path: '/login',
    component: () => import('@/views/Authentication/LoginView.vue'),
    meta: { title: 'Login' }
  },
  {
    name: 'callback',
    path: '/callback',
    component: () => import('@/views/Authentication/LoginView.vue'),
    beforeEnter: async (to, from, next) => {
      const { authCallbackGuard } = await import(
        '@/router/guards/authCallbackGuard'
      )
      const result = await authCallbackGuard(to)
      if (result === false) {
        next(false)
      } else {
        next(result)
      }
    }
  },
  {
    name: 'Default',
    path: '/',
    component: () => import('@/layouts/Default.vue'),
    beforeEnter: async (to, from, next) => {
      const appStore = useAppStore()
      const portalService = new PortalService()
      const portalDomain = import.meta.env.DEV
        ? import.meta.env.VITE_PORTAL_DOMAIN || 'roimatic.adbox.pro'
        : window.location.hostname
      try {
        appStore.setPortal(await portalService.getPortalData(portalDomain))
      } catch (error) {
        if (error instanceof InvalidPortalError) {
          next({ name: 'NotFound' })
          return
        }
      }
      next()
    },
    children: [
      authRoutes,
      {
        path: 'app/:businessProfileId',
        name: 'App',
        component: () => import('@/layouts/AppLayout.vue'),
        children: [
          samplePageRoutes,
          productSetRoutes,
          ...productsetCreationRoutes,
          productInsightsRoutes,
          AudienceListingRoute,
          AudienceCreationRoute,
          editAudienceRoute,
          ...catalogManagerRoutes,
          ...productVariantsRoutes,
          sourceCatalogListingRoute,
          dashboardRoutes,
          ...stopLossConfigurationsRoute,
          ...creativeAutomationListingRoute
        ],
        beforeEnter: async (to, from, next) => {
          const businessProfileId = Number(to.params.businessProfileId)
          if (isNaN(businessProfileId)) {
            next({ name: 'NotFound' })
            return
          }
          if (to.matched[to.matched.length - 1].name === 'App') {
            next({ name: 'Catalogs', params: { businessProfileId } })
            return
          }
          const appStore = useAppStore()
          const userStore = useUserStore()

          let userId = userStore.user?.id
          if (!userId) {
            try {
              const user = await userService.getCurrentUser()
              if (user) {
                userStore.setUser(user)
                userId = user.id
              }
            } catch (e) {
              if (isForbiddenPortalError(e)) {
                next({ name: 'NoAccess', replace: true })
                NProgress.done()
                return
              }
            }
          }

          if (appStore.businessProfiles.length === 0 && userId) {
            const businessProfileService = new BusinessProfileService()
            appStore.setBusinessProfiles(
              await businessProfileService.getUserBusinessProfiles(
                appStore.getPortal?.id,
                userId,
                true
              )
            )
          }

          // check user bp access
          if (
            !appStore.getBusinessProfiles
              .map(bp => bp.id)
              .includes(businessProfileId)
          ) {
            appRouter?.push({ name: 'NotFound' })
            throw new InvalidBusinessProfileRequestedError(
              'User does not have access to the selected business profile.'
            )
          }

          //fetch roimaticConfiguration
          if (
            !appStore.roimaticConfiguration ||
            appStore.getSelectedBusinessProfileId !=
              Number(to.params.businessProfileId)
          ) {
            const roimaticConfigResponse =
              await roimaticService.getRoimaticConfiguration(
                Number(to.params.businessProfileId)
              )
            appStore.setRoimaticConfiguraiton(roimaticConfigResponse)
          }

          const currencyService = new CurrencyService()
          const selectedBusinessProfileId = Number(to.params.businessProfileId)
          const bpChange =
            appStore.getSelectedBusinessProfileId !== selectedBusinessProfileId

          if (bpChange) {
            appStore.setSelectedBusinessProfileId(selectedBusinessProfileId)
          }

          // set bp currency
          if (!appStore.getSelectedBusinessProfileCurrency || bpChange) {
            appStore.setSelectedBusinessProfileCurrency(
              await currencyService.getBusinessProfileCurrency(
                selectedBusinessProfileId,
                appStore.getSelectedBusinessProfile?.currencyId ?? 17
              )
            )
          }

          // set bp timezone
          if (!appStore.selectedBusinessProfileTimezone || bpChange) {
            const timeZoneService = new TimeZoneService()
            const timezones = await timeZoneService.getTimezones()
            const timeZoneId =
              appStore.getSelectedBusinessProfile?.timezoneId || 142
            appStore.setSelectedBusinessProfileTimezone(
              timezones.find(x => x.id === timeZoneId) ?? null
            )
          }

          next()
        }
      }
    ]
  },
  oAuthRedirectRoutes,
  signupRoutes,
  {
    path: '/no-access',
    name: 'NoAccess',
    component: () => import('@/views/Error/PageNotFound.vue'),
    meta: { isNoAccess: true }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/Error/PageNotFound.vue')
  }
]

let appRouter: ReturnType<typeof createRouter> | null = null

const createAppRouter = () => {
  const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes
  })

  router.beforeEach(async (to, from, next) => {
    NProgress.start()
    if (to.meta.title) {
      document.title = to.meta.title as string
    }

    const isPublic =
      publicRoutes.includes(to.name as string) ||
      to.fullPath === '/app/health' ||
      to.path === '/signup' ||
      to.path === '/oAuthRedirect' ||
      to.path === '/login' ||
      to.path === '/callback'

    const token = packageAuthService.getAccessToken()

    if (!token && !isPublic) {
      next({ name: 'login', query: { redirect: to.fullPath } })
      NProgress.done()
      return
    }

    if (token && to.name !== 'NoAccess') {
      const userStore = useUserStore()
      if (!userStore.user) {
        try {
          const user = await userService.getCurrentUser()
          if (user) {
            userStore.setUser(user)
          }
        } catch (e) {
          if (isForbiddenPortalError(e)) {
            next({ name: 'NoAccess', replace: true })
            NProgress.done()
            return
          }
        }
      }
    }

    next()
    NProgress.done()
  })

  appRouter = router
  return router
}

export { appRouter }
export default createAppRouter
